import { colors, spacing } from "@procore/core-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppStore } from "app/redux-store-interactions";
import { HEADER_BORDER } from "components/window/constants";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHighlighted, useLazyHighlighted } from "contexts/highlighted";
import { captureVisibilitySnapshot, isolateSimilarInXray } from "utils/objectActions";

import { useGroupExpansion } from "../hooks/useGroupExpansion";
import { usePropertyFiltering } from "../hooks/usePropertyFiltering";
import { usePropertySelection } from "../hooks/usePropertySelection";
import type { PropertiesTabProps } from "../types";
import { BulkActionsDropdown } from "./BulkActionsDropdown";
import { PropertyGroupList } from "./PropertyGroupList";

export function PropertiesTabContent({ object, searchTerm, isSearchActive, onEmptyStateChange }: PropertiesTabProps) {
    const { t } = useTranslation();
    const dispatchHighlighted = useDispatchHighlighted();
    // Lazy: the highlight changes as a *result* of a toggle, so reading it reactively would
    // re-render this tree on every one of its own searches.
    const lazyHighlighted = useLazyHighlighted();
    const store = useAppStore();
    // The in-flight properties search, if any — aborted and replaced by every new toggle so
    // a superseded search doesn't keep running just to have its result discarded.
    const filterSearchRef = useRef<AbortController>();
    const {
        state: { dataAccess },
    } = useExplorerGlobals(true);

    // Create grouped properties for filtering
    const groupedProperties = useMemo(() => {
        if (!object) {
            return [];
        }

        const groups = [];

        const baseProps = object.base.filter(([, value]) => value);
        if (baseProps.length > 0) {
            groups.push({
                name: t("general"),
                displayName: t("general"),
                properties: baseProps,
                isGeneral: true,
            });
        }

        Object.entries(object.grouped).forEach(([groupName, group]) => {
            const filteredProps = group.properties.filter(([, value]) => value);
            if (filteredProps.length > 0) {
                groups.push({
                    name: groupName,
                    displayName: group.name,
                    properties: filteredProps,
                });
            }
        });

        return groups;
    }, [object, t]);

    const { filteredGroups, hasSearchResults } = usePropertyFiltering(groupedProperties, searchTerm);
    const { isPropertySelected, getGroupStats, togglePropertySelection, toggleGroupSelection } = usePropertySelection(
        filteredGroups,
        {
            onSelectionChange: (next, before) => {
                filterSearchRef.current?.abort();
                const controller = new AbortController();
                filterSearchRef.current = controller;

                const visibility = captureVisibilitySnapshot(store.getState());
                void isolateSimilarInXray(next, {
                    dataAccess,
                    store,
                    dispatchHighlighted,
                    signal: controller.signal,
                    before: {
                        propertyFilter: before,
                        highlightedIds: lazyHighlighted.current.idArr,
                        visibility,
                    },
                });
            },
        },
    );

    const { isGroupExpanded, toggleGroupExpanded } = useGroupExpansion();

    // Sorted by label: the raw map key stays the find-similar identifier, and the two can
    // disagree about encoding (`Pset%2fDuct` vs `Pset/Duct`).
    const sortedGroups = useMemo(
        () =>
            filteredGroups.toSorted((a, b) =>
                a.displayName.localeCompare(b.displayName, "en", { sensitivity: "accent" }),
            ),
        [filteredGroups],
    );

    // Both counts describe what's on screen, so they stay coherent under an active search.
    const selectedCount = sortedGroups.reduce((sum, group) => sum + getGroupStats(group.name).selected, 0);
    const totalCount = filteredGroups.reduce((sum, group) => sum + group.properties.length, 0);

    // Communicate search empty state to parent
    useEffect(() => {
        if (onEmptyStateChange && isSearchActive) {
            onEmptyStateChange(!hasSearchResults);
        }
    }, [hasSearchResults, isSearchActive, onEmptyStateChange]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
            }}
        >
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    padding: spacing.md,
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    borderBottom: HEADER_BORDER,
                    backgroundColor: selectedCount > 0 ? colors.blue98 : colors.white,
                }}
            >
                <BulkActionsDropdown selectedCount={selectedCount} totalCount={totalCount} />
            </div>
            <div
                style={{
                    flex: 1,
                    padding: spacing.lg,
                    display: "flex",
                    flexDirection: "column",
                    gap: spacing.sm,
                    overflowY: "auto",
                }}
            >
                <PropertyGroupList
                    groups={sortedGroups}
                    searchTerm={searchTerm}
                    isSearchActive={isSearchActive}
                    isGroupExpanded={isGroupExpanded}
                    getGroupStats={getGroupStats}
                    isPropertySelected={isPropertySelected}
                    onToggleGroupExpansion={toggleGroupExpanded}
                    onToggleGroupSelection={toggleGroupSelection}
                    onTogglePropertySelection={togglePropertySelection}
                />
            </div>
        </div>
    );
}
